import { useState, useEffect, useCallback } from 'react'
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Box,
	Typography,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	TextField,
	Alert,
	Card,
	CardContent,
	Chip,
	Stack,
	Grid
} from '@mui/material'
import { supabase } from "../utils/supabase"
import DatePicker from './ui/DatePicker'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

function AddAppointment({ open, onClose, merchant, onAppointmentAdded }) {
	// Service & Time Selection
	const [services, setServices] = useState([])
	const [selectedService, setSelectedService] = useState('')
	const [selectedDate, setSelectedDate] = useState('')
	const [availableSlots, setAvailableSlots] = useState([])
	const [selectedSlot, setSelectedSlot] = useState('')
	const [loadingSlots, setLoadingSlots] = useState(false)

	// Customer Information
	const [customerName, setCustomerName] = useState('')
	const [customerPhone, setCustomerPhone] = useState('')

	// General state
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState('')

	const resetForm = () => {
		setSelectedService('')
		setSelectedDate('')
		setSelectedSlot('')
		setAvailableSlots([])
		setCustomerName('')
		setCustomerPhone('')
		setError('')
	}

	const loadServices = useCallback(async () => {
		try {
			const { data, error } = await supabase
				.from('service_types')
				.select('*')
				.eq('merchant_id', merchant.id)
				.order('name')

			if (error) throw error
			setServices(data || [])
		} catch (e) {
			console.error('Error loading services:', e)
			setError('Failed to load services')
		}
	}, [merchant?.id])

	const loadAvailableSlots = useCallback(async () => {
		setLoadingSlots(true)
		try {
			const service = services.find(s => s.id === selectedService)
			if (!service) return

			// Generate time slots for the selected date
			const date = dayjs(selectedDate)
			const slots = []

			// Generate slots from 8 AM to 6 PM in 5-minute intervals
			for (let hour = 8; hour < 18; hour++) {
				for (let minute = 0; minute < 60; minute += 5) {
					const slotTime = date.hour(hour).minute(minute)
					const slotTimeUTC = slotTime.tz(merchant.timezone).utc()

					// Check if this slot is available using the merchant_slot_free_count function
					const { data, error } = await supabase.rpc('merchant_slot_free_count', {
						p_merchant_id: merchant.id,
						p_service_id: selectedService,
						p_start_ts: slotTimeUTC.toISOString()
					})
					console.log('data', data)

					if (!error && data > 0) {
						slots.push({
							time: slotTime.format('h:mm A'),
							utcTime: slotTimeUTC.toISOString(),
							available: data
						})
					}
				}
			}

			setAvailableSlots(slots)
		} catch (e) {
			console.error('Error loading slots:', e)
			setError('Failed to load available time slots')
		} finally {
			setLoadingSlots(false)
		}
	}, [selectedService, selectedDate, merchant, services])

	// Load services when merchant changes
	useEffect(() => {
		if (merchant?.id && open) {
			loadServices()
		}
	}, [merchant, open, loadServices])

	// Reset form when opening
	useEffect(() => {
		if (open) {
			resetForm()
		}
	}, [open])

	// Load available slots when service or date changes
	useEffect(() => {
		if (selectedService && selectedDate && merchant) {
			loadAvailableSlots()
		} else {
			setAvailableSlots([])
			setSelectedSlot('')
		}
	}, [selectedService, selectedDate, merchant, loadAvailableSlots])

	const handleSubmit = async () => {
		// Validate all fields
		if (!selectedService) {
			setError('Please select a service')
			return
		}
		if (!selectedDate) {
			setError('Please select a date')
			return
		}
		if (!selectedSlot) {
			setError('Please select a time slot')
			return
		}
		if (!customerName.trim()) {
			setError('Customer name is required')
			return
		}
		if (!customerPhone.trim()) {
			setError('Customer phone number is required')
			return
		}

		setLoading(true)
		setError('')

		try {
			// Use the create_appointment function
			const { data, error } = await supabase.rpc('create_appointment', {
				p_user_id: null, // Manual appointment, no user account
				p_merchant_id: merchant.id,
				p_service_id: selectedService,
				p_start_ts: selectedSlot
			})

			if (error) throw error

			// Update the appointment with customer details
			const { error: updateError } = await supabase
				.from('appointments')
				.update({
					customer_name: customerName.trim(),
					phone_number: customerPhone.trim()
				})
				.eq('id', data)

			if (updateError) throw updateError

			// Success - close modal and refresh appointments
			onClose()
			if (onAppointmentAdded) {
				onAppointmentAdded()
			}
		} catch (e) {
			console.error('Error creating appointment:', e)
			setError(e.message || 'Failed to create appointment')
		} finally {
			setLoading(false)
		}
	}

	const getMinDate = () => {
		return dayjs().format('YYYY-MM-DD')
	}

	const getMaxDate = () => {
		return dayjs().add(60, 'days').format('YYYY-MM-DD')
	}

	const selectedServiceObj = services.find(s => s.id === selectedService)

	return (
		<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle>
				<Typography variant="h6">Add New Appointment</Typography>
			</DialogTitle>

			<DialogContent>
				{error && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{error}
					</Alert>
				)}

				<Grid container spacing={3}>
					{/* Left Column - Service & Time Selection */}
					<Grid item xs={12} md={6}>
						<Typography variant="h6" gutterBottom>
							Service & Time
						</Typography>

						{/* Service Selection */}
						<FormControl fullWidth sx={{ mb: 2 }}>
							<InputLabel>Service *</InputLabel>
							<Select
								value={selectedService}
								label="Service *"
								onChange={(e) => setSelectedService(e.target.value)}
							>
								{services.map((service) => (
									<MenuItem key={service.id} value={service.id}>
										{service.name} ({service.duration_minutes} min)
									</MenuItem>
								))}
							</Select>
						</FormControl>

						{/* Date Selection */}
						<DatePicker
							label="Date *"
							value={selectedDate}
							onChange={(e) => setSelectedDate(e.target.value)}
							minDate={getMinDate()}
							maxDate={getMaxDate()}
							placeholder="Select appointment date"
							helperText="Choose a date within the next 60 days"
							sx={{ mb: 2 }}
						/>

						{/* Time Slot Selection */}
						{selectedService && selectedDate && (
							<Box>
								<Typography variant="subtitle1" gutterBottom>
									Available Time Slots *
								</Typography>
								{loadingSlots ? (
									<Typography variant="body2" color="text.secondary">
										Loading available slots...
									</Typography>
								) : availableSlots.length === 0 ? (
									<Typography variant="body2" color="text.secondary">
										No available slots for this date. Please select a different date.
									</Typography>
								) : (
									<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, maxHeight: '200px', overflowY: 'auto' }}>
										{availableSlots.map((slot) => (
											<Chip
												key={slot.utcTime}
												label={slot.time}
												clickable
												color={selectedSlot === slot.utcTime ? 'primary' : 'default'}
												variant={selectedSlot === slot.utcTime ? 'filled' : 'outlined'}
												onClick={() => setSelectedSlot(slot.utcTime)}
											/>
										))}
									</Box>
								)}
							</Box>
						)}
					</Grid>

					{/* Right Column - Customer Information */}
					<Grid item xs={12} md={6}>
						<Typography variant="h6" gutterBottom>
							Customer Information
						</Typography>

						{/* Customer Form */}
						<TextField
							fullWidth
							label="Customer Name *"
							value={customerName}
							onChange={(e) => setCustomerName(e.target.value)}
							sx={{ mb: 2 }}
							placeholder="Enter customer's full name"
						/>

						<TextField
							fullWidth
							label="Phone Number *"
							value={customerPhone}
							onChange={(e) => setCustomerPhone(e.target.value)}
							placeholder="Enter customer's phone number"
							helperText="Required for appointment confirmation and reminders"
							sx={{ mb: 2 }}
						/>

						{/* Appointment Summary */}
						{selectedServiceObj && selectedDate && selectedSlot && (
							<Card sx={{ bgcolor: 'primary.50' }}>
								<CardContent>
									<Typography variant="subtitle2" gutterBottom>
										Appointment Summary
									</Typography>
									<Stack spacing={1}>
										<Typography variant="body2">
											<strong>Service:</strong> {selectedServiceObj.name}
										</Typography>
										<Typography variant="body2">
											<strong>Date:</strong> {dayjs(selectedDate).format('dddd, MMMM D, YYYY')}
										</Typography>
										<Typography variant="body2">
											<strong>Time:</strong> {dayjs(selectedSlot).tz(merchant?.timezone).format('h:mm A')} ({merchant?.timezone})
										</Typography>
										<Typography variant="body2">
											<strong>Duration:</strong> {selectedServiceObj.duration_minutes} minutes
										</Typography>
									</Stack>
								</CardContent>
							</Card>
						)}
					</Grid>
				</Grid>
			</DialogContent>

			<DialogActions>
				<Button onClick={onClose} disabled={loading}>
					Cancel
				</Button>
				<Button
					onClick={handleSubmit}
					variant="contained"
					disabled={loading}
				>
					{loading ? 'Adding...' : 'Add Appointment'}
				</Button>
			</DialogActions>
		</Dialog>
	)
}

export default AddAppointment
